import onetime from "@-/com.npmjs.onetime";
import {
  DeadlineExceededError,
  notifyDeadlineExceeded,
} from "@-/dev.improve.haniverse.lib.deadline";

/**
 * A synchronous "a review is being written" latch, shared by every rating path.
 *
 * React Query's `isPending` only flips after a render, so two presses landing in the same frame
 * both see `false` and both commit — recording the same review twice and double-advancing FSRS.
 * A synchronous module-level latch is the only thing that closes that window.
 *
 * Deliberately ONE latch across all rating entry points rather than one per hook: the
 * single-card rating buttons and the sentence drill's batch rating can both be reachable at the
 * same moment, and two independent latches would let one of each through.
 *
 * The latch is a LEASE, not a flag. It used to be a boolean released only by its holder, so a
 * holder that wedged (a write that never answered) or forgot to release silently dropped every
 * rating in the app until restart. A lease expires on its own after `RATING_LATCH_LEASE_MS`, the
 * expiry is reported as a hang, and a released-late or expired lease can never release its
 * successor.
 */

/**
 * The longest a rating may legitimately hold the latch: its bounded write (8s in the Learn deck),
 * then preparing and navigating to the next card, whose queries are bounded at 30s. Past this the
 * holder is wedged, and dropping every later rating would be worse than letting one through.
 */
export const RATING_LATCH_LEASE_MS = 45_000;

export type RatingLatchLease = {
  /** Release this lease. Idempotent, and a no-op once the lease expired or was superseded. */
  release: () => void;
};

type LeaseState = { id: number; timer: ReturnType<typeof setTimeout> };

const getLatch = onetime(() => ({
  current: null as LeaseState | null,
  nextId: 1,
}));

function releaseLease(id: number): void {
  const latch = getLatch();
  if (latch.current?.id !== id) {
    return;
  }
  clearTimeout(latch.current.timer);
  latch.current = null;
}

/**
 * Take the latch, or `null` when a rating is already in progress. Truthy on success, so existing
 * `if (!tryAcquireRatingLatch()) return` call sites keep working; new code should keep the
 * returned lease and call `lease.release()` (or `releaseRatingLatch(lease)`).
 */
export function tryAcquireRatingLatch(): RatingLatchLease | null {
  const latch = getLatch();
  if (latch.current !== null) {
    return null;
  }
  const id = latch.nextId++;
  const timer = setTimeout(() => {
    if (latch.current?.id !== id) {
      return;
    }
    latch.current = null;
    notifyDeadlineExceeded(new DeadlineExceededError("rating latch lease", RATING_LATCH_LEASE_MS));
  }, RATING_LATCH_LEASE_MS);
  // The lease must not keep a Jest/Node worker alive; browsers and Hermes return a numeric id.
  const unref: unknown =
    typeof timer === "object" && timer !== null ? Reflect.get(timer, "unref") : undefined;
  if (typeof unref === "function") {
    Reflect.apply(unref, timer, []);
  }
  latch.current = { id, timer };
  return {
    release: () => {
      releaseLease(id);
    },
  };
}

/**
 * Release the latch. Pass the lease from `tryAcquireRatingLatch` so a holder whose lease already
 * expired cannot release the NEXT rating's lease. Without an argument (legacy callers) it releases
 * whichever lease is current.
 */
export function releaseRatingLatch(lease?: RatingLatchLease): void {
  if (lease !== undefined) {
    lease.release();
    return;
  }
  const current = getLatch().current;
  if (current !== null) {
    releaseLease(current.id);
  }
}
